/**
 * THE WORKING MARK'S ONE STYLESHEET — every spinner on prest0.ai is the logo's zero, turning (Justin, 2026-09-26;
 * design record D-71, docs/PLAN-design-system-20260922.md §7a.10).
 *
 * His ask, verbatim intent: "Improve and unify the design associated with the spinning circle that more aligns with the
 * logo design", and, of the old ring, "it keeps resetting awkwardly like it would rotate then reset to original
 * situation every second and it should be much more smooth, intuitive, modern experience — visually appealing." His
 * three choices, from options: the mark turns and settles on the logo; the whole app, the chat first; quiet ink.
 *
 * WHAT IS DRAWN. The mark exactly (src/design/pavilion/markGeometry.ts): its ink arc and its gold arc, round-capped, on
 * one circle, each laid on the spinner as a mask — the ink on `::before`, the gold on `::after`, so the gold's round
 * ends sit over the ink's as they do in the logo. The masks' box is the ring itself, so a 16 px spinner is a 16 px
 * zero and its stroke is the logo's proportion. Drawn by class on an empty element
 * (src/components/common/markSpinnerMarkup.ts), never by svg: the chat's sanitiser strips svg, and an inline mask,
 * from a message's html.
 * Every browser the platform supports has `mask-image` (unprefixed, or `-webkit-` in WebKit), and a mask that has not
 * decoded hides its layer rather than painting a square.
 *
 * COLOUR BY ROLE. The ink arc is the page's ink at about 40% (the owner's "quiet ink"), never the colour of the words
 * beside it; the gold arc is the satin, laid over the arc's own height as the logo lays it. Four properties carry the
 * inks (`--mark-spin-*`), and a surface sets them once for every spinner inside it. The defaults are the light-ground
 * inks (MARK_INKS_ON_LIGHT: the noon roles, `--color-*`, which theme.ts writes on :root from the same token twin the new
 * design reads), where the satin takes its deep end so the moving arc clears 3:1 on a light ground; a surface that is
 * dark in every light sets MARK_INKS_ON_DARK, and the new design's chat skin sets its own per light
 * (pavilionChatCss.ts). `is-mono` draws both arcs in the words' own colour (a control); `is-warn` draws the gold arc in
 * the warning ink (a retry that is waiting).
 *
 * MOTION. One lap every 1.8 s, eased in and out so each lap slows through the logo's own pose (the markup module
 * explains the curve), on ONE clock: src/utils/markSpinnerPhase.ts starts every `pendingSpin` animation at the same
 * instant of the page's timeline, so a spinner rebuilt by a repaint carries on where the last one was — the reset the
 * owner saw — and every spinner on screen turns in step. The timing is `!important` so a row stored before this
 * change, which names `pendingSpin 0.8s linear` inline, turns the same way; its inline ring is switched off the same
 * way. Reduced motion stops the turn, leaving the logo; forced colours draw the mark in the system's text colour.
 *
 * `pendingSpin` is a protocol word (see markSpinnerMarkup.ts): these keyframes are its one definition.
 * Pinned by tests/workingMark/sheet.test.ts and, in a browser, scripts/check-mark-spinner.mjs.
 */
import { MARK_GOLD_ARC, MARK_GOLD_Y1, MARK_GOLD_Y2, MARK_INK_ARC, MARK_RADIUS, MARK_STROKE } from '../design/pavilion/markGeometry';
import {
  MARK_INKS_ON_LIGHT,
  MARK_INK_OPACITY,
  MARK_SPINNER_CLASS,
  MARK_SPINNER_SIZES_PX,
  MARK_SPIN_ANIMATION,
  MARK_TURN_EASING,
  MARK_TURN_MS,
} from '../components/common/markSpinnerMarkup';

/** The ring's outer edge: the circle and half the stroke on every side — 88 of the logo's 100 units. */
const RING = 2 * (MARK_RADIUS + MARK_STROKE / 2);
const PAD = 50 - RING / 2;
/** The masks' box: the ring itself, so the spinner's box is its zero. */
export const MARK_RING_VIEWBOX = `${PAD} ${PAD} ${RING} ${RING}`;

/** One arc as a mask: an opaque round-capped stroke and nothing else — the paint is the spinner's own. */
export function markArcMask(d: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='${MARK_RING_VIEWBOX}'>`
    + `<path d='${d}' fill='none' stroke='black' stroke-width='${MARK_STROKE}' stroke-linecap='round'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

export const MARK_INK_MASK = markArcMask(MARK_INK_ARC);
export const MARK_GOLD_MASK = markArcMask(MARK_GOLD_ARC);

/** Where a height in the logo's box lands in the spinner's box, as a percentage. */
const at = (y: number): string => `${Math.round(((y - PAD) / RING) * 1000) / 10}%`;

/** One of the mark's inks, falling back to its light-ground value (markSpinnerMarkup.ts MARK_INKS_ON_LIGHT). */
const ink = (name: keyof typeof MARK_INKS_ON_LIGHT): string => `var(${name}, ${MARK_INKS_ON_LIGHT[name]})`;

/**
 * The satin over the gold arc: its lit top, its body at the arc's middle, its deep end at the arc's foot. The lit stop
 * sits a little above the arc (10%) so the arc's own top is already a step into the body: on a light ground that keeps
 * the moving arc at 3:1 or better against the worst pixel under it, and at dusk it is still the logo's lit satin.
 * Exported for the mark's one twin, the boot splash (public/index.html), which draws before any stylesheet loads.
 */
export function markGoldPaint(hi: string, body: string, lo: string): string {
  return `linear-gradient(180deg, ${hi} 10%, ${body} ${at((MARK_GOLD_Y1 + MARK_GOLD_Y2) / 2)}, ${lo} 100%)`;
}
const GOLD_PAINT = markGoldPaint(ink('--mark-spin-gold-hi'), ink('--mark-spin-gold-body'), ink('--mark-spin-gold-lo'));

/**
 * The hosts: the one class, and a branch glyph a stored pill wrote before this change (its html is rebuilt on the next
 * repaint; until then it turns like every other spinner).
 */
const HOSTS = [`.${MARK_SPINNER_CLASS}`, '.pending-branch-glyph.is-spin'];
const on = (suffix = ''): string => HOSTS.map((host) => `${host}${suffix}`).join(', ');
const C = `.${MARK_SPINNER_CLASS}`;
const px = MARK_SPINNER_SIZES_PX;

export const MARK_SPINNER_CSS = [
  `@keyframes ${MARK_SPIN_ANIMATION} { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`,
  `${on()} { position: relative; display: inline-block; flex-shrink: 0; box-sizing: border-box; vertical-align: middle;
  width: ${px.md}px; height: ${px.md}px; pointer-events: none;
  border: 0 !important; border-radius: 0 !important; background: none !important;
  color: ${ink('--mark-spin-ink')};
  animation-name: ${MARK_SPIN_ANIMATION}; animation-duration: ${MARK_TURN_MS}ms !important;
  animation-timing-function: ${MARK_TURN_EASING} !important; animation-iteration-count: infinite !important; }`,
  `${on('::before')}, ${on('::after')} { content: ""; position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  -webkit-mask-size: 100% 100%; mask-size: 100% 100%; -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat; }`,
  `${on('::before')} { -webkit-mask-image: ${MARK_INK_MASK}; mask-image: ${MARK_INK_MASK};
  background: currentColor; opacity: var(--mark-spin-ink-alpha, ${MARK_INK_OPACITY}); }`,
  `${on('::after')} { -webkit-mask-image: ${MARK_GOLD_MASK}; mask-image: ${MARK_GOLD_MASK}; background: ${GOLD_PAINT}; }`,
  `${C}.is-xs { width: ${px.xs}px; height: ${px.xs}px; } ${C}.is-sm { width: ${px.sm}px; height: ${px.sm}px; }
${C}.is-lg { width: ${px.lg}px; height: ${px.lg}px; } ${C}.is-xl { width: ${px.xl}px; height: ${px.xl}px; }`,
  `${C}.is-mono { color: inherit; } ${C}.is-mono::after { background: currentColor; }`,
  `${C}.is-warn::after { background: var(--mark-spin-warn, var(--color-warning-ink)); }`,
  `@media (forced-colors: active) { ${on('::before')}, ${on('::after')} { forced-color-adjust: none; background: CanvasText; } }`,
  `@media (prefers-reduced-motion: reduce) { ${on()} { animation-name: none !important; } }`,
].join('\n');

/** What a twin of the mark draws with: literal colours, because it draws before any stylesheet (or variable) loads. */
export interface MarkTwinInks {
  ink: string;
  goldHi: string;
  goldBody: string;
  goldLo: string;
}

/**
 * THE MARK'S ONE TWIN — the boot splash's spinner (public/index.html, `#app-splash.ws-transition .ws-spin`), drawn
 * while the page reloads during an organization switch, before any bundle or stylesheet has loaded. It is the mark as
 * this sheet draws it — the same masks, the same satin, the same quiet ink, the same lap and curve, the same reduced
 * motion and forced colours — for one element, in literal colours, minified to sit in the splash's inline `<style>`.
 * The page carries its output VERBATIM: tests/workspaces/splashBootContinuity.test.ts computes this and requires it
 * there, so a change to the mark that forgets the splash fails there with the rules to paste. Nothing in the app calls
 * it (it is dropped from the bundle).
 */
export function markTwinCss({ selector, keyframes, sizePx, inks }: {
  selector: string;
  keyframes: string;
  sizePx: number;
  inks: MarkTwinInks;
}): string {
  const s = selector;
  return [
    `@keyframes ${keyframes}{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`,
    `${s}{position:relative;display:inline-block;flex-shrink:0;box-sizing:border-box;width:${sizePx}px;height:${sizePx}px;`
      + `color:${inks.ink};animation:${keyframes} ${MARK_TURN_MS}ms ${MARK_TURN_EASING} infinite}`,
    `${s}::before,${s}::after{content:"";position:absolute;top:0;left:0;width:100%;height:100%;`
      + '-webkit-mask-size:100% 100%;mask-size:100% 100%;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat}',
    `${s}::before{-webkit-mask-image:${MARK_INK_MASK};mask-image:${MARK_INK_MASK};background:currentColor;opacity:${MARK_INK_OPACITY}}`,
    `${s}::after{-webkit-mask-image:${MARK_GOLD_MASK};mask-image:${MARK_GOLD_MASK};`
      + `background:${markGoldPaint(inks.goldHi, inks.goldBody, inks.goldLo)}}`,
    `@media (forced-colors:active){${s}::before,${s}::after{forced-color-adjust:none;background:CanvasText}}`,
    `@media (prefers-reduced-motion:reduce){${s}{animation:none}}`,
  ].join('\n');
}
